/**
 * DispatchBoard Component
 *
 * Main container for the dispatch board feature
 *
 * Features:
 * - Drag-and-drop context
 * - Multiple driver columns
 * - Unassigned jobs column
 * - Add driver functionality
 * - Responsive horizontal scroll
 * - Loading and error states
 */

import { useState } from 'react';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw } from 'lucide-react';
import { useDispatchBoard } from '../hooks/useDispatchBoard';
import { useJobAssignments } from '../hooks/useJobAssignments';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import { BoardColumn } from './BoardColumn';
import { JobCard } from './JobCard';
import { AddDriverDialog } from './AddDriverDialog';
import { RemoveDriverDialog } from './RemoveDriverDialog';
import type { JobAssignment } from '../types/dispatch.types';

export function DispatchBoard() {
  const [addDriverDialogOpen, setAddDriverDialogOpen] = useState(false);
  const [removeDriverDialog, setRemoveDriverDialog] = useState<{
    open: boolean;
    driverName: string;
  }>({ open: false, driverName: '' });

  // Fetch board and assignments
  const {
    board,
    isLoading: isBoardLoading,
    error: boardError,
    refetch,
    addDriver,
    removeDriver,
    initializeBoard,
    isInitializing,
    isAddingDriver,
    isRemovingDriver,
  } = useDispatchBoard();

  const {
    assignments,
    isLoading: isAssignmentsLoading,
    error: assignmentsError,
    reassignJob,
    unassignJob,
  } = useJobAssignments();

  // Drag and drop
  const handleReassignJob = (assignmentId: string, newDriverId: string, estimatedStartTime: string) => {
    reassignJob({ assignmentId, request: { newDriverId, estimatedStartTime } });
  };

  const { sensors, activeAssignment, handleDragStart, handleDragEnd, handleDragCancel } = useDragAndDrop({
    onReassignJob: handleReassignJob,
    assignments,
  });

  // Group assignments by driver
  const getAssignmentsByDriver = (driverName: string): JobAssignment[] => {
    return assignments.filter((a) => a.driverId === driverName);
  };

  const unassignedJobs = assignments.filter((a) => a.driverId === 'unassigned' || !a.driverId);

  // Handle remove driver
  const handleRemoveDriverClick = (driverName: string) => {
    setRemoveDriverDialog({ open: true, driverName });
  };

  const handleRemoveDriverConfirm = () => {
    removeDriver(removeDriverDialog.driverName);
  };

  const getAssignmentCountForDriver = (driverName: string) => {
    return getAssignmentsByDriver(driverName).length;
  };

  // Loading state
  if (isBoardLoading || isAssignmentsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">Loading dispatch board...</p>
        </div>
      </div>
    );
  }

  // Error state - check if board doesn't exist (404)
  if (boardError || assignmentsError) {
    const errorMessage = (boardError as Error)?.message || (assignmentsError as Error)?.message || 'Failed to load dispatch board';
    const isBoardNotFound = errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('404');

    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center max-w-md">
          <p className="text-gray-700 mb-2 font-semibold">
            {isBoardNotFound ? 'Dispatch Board Not Initialized' : 'Error Loading Dispatch Board'}
          </p>
          <p className="text-gray-600 mb-4 text-sm">
            {isBoardNotFound
              ? 'Your dispatch board needs to be initialized before you can start assigning jobs to drivers.'
              : errorMessage
            }
          </p>
          <div className="flex gap-2 justify-center">
            {isBoardNotFound ? (
              <Button
                onClick={() => initializeBoard()}
                disabled={isInitializing}
              >
                {isInitializing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Initializing...
                  </>
                ) : (
                  'Initialize Board'
                )}
              </Button>
            ) : (
              <Button onClick={() => refetch()}>Try Again</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Empty board state
  if (!board) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-gray-500 mb-4">No dispatch board found</p>
          <Button onClick={() => refetch()}>Refresh</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Dispatch Board</h2>
          <p className="text-gray-600 text-sm">
            Drag jobs to assign them to drivers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            aria-label="Refresh board"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            onClick={() => setAddDriverDialogOpen(true)}
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Driver
          </Button>
        </div>
      </div>

      {/* Board Columns */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-min">
            {/* Unassigned Column */}
            <BoardColumn
              driverName="unassigned"
              assignments={unassignedJobs}
              onUnassignJob={(jobId) => unassignJob({ jobId })}
              isUnassignedColumn
            />

            {/* Driver Columns */}
            {board.columns
              .sort((a, b) => a.order - b.order)
              .map((column) => (
                <BoardColumn
                  key={column.id}
                  driverName={column.driverName}
                  assignments={getAssignmentsByDriver(column.driverName)}
                  onRemoveDriver={() => handleRemoveDriverClick(column.driverName)}
                  onUnassignJob={(jobId) => unassignJob({ jobId })}
                />
              ))}
          </div>
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeAssignment ? (
            <div className="rotate-3 opacity-80">
              <JobCard
                assignment={activeAssignment}
                onUnassign={() => {}}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Dialogs */}
      <AddDriverDialog
        open={addDriverDialogOpen}
        onOpenChange={setAddDriverDialogOpen}
        onAddDriver={addDriver}
        isLoading={isAddingDriver}
      />

      <RemoveDriverDialog
        open={removeDriverDialog.open}
        onOpenChange={(open) => setRemoveDriverDialog({ ...removeDriverDialog, open })}
        driverName={removeDriverDialog.driverName}
        assignmentCount={getAssignmentCountForDriver(removeDriverDialog.driverName)}
        onConfirm={handleRemoveDriverConfirm}
        isLoading={isRemovingDriver}
      />
    </div>
  );
}
